import { type ReactElement, useState } from 'react'
import {
  type ConversationData,
  type ConversationResolution,
  type ConversationResolveLocationFlagged,
  ConversationResolveLocationFlaggedSchema,
  isDefined,
  toResponseError,
} from '@terros/common'
import { Modal, Stack, Text } from '@mantine/core'
import { useResolveConversation, useTranslation } from '@front/model'
import { TAlert } from '@components'
import { ReviewResolutionForm } from './ReviewResolutionForm'

type Props = {
  conversation: ConversationData
  onClose: VoidFunction
  onResolved: (message: string) => void
}

export function ReviewConversation({ conversation, onClose, onResolved }: Props): ReactElement {
  const { t } = useTranslation()
  const mutation = useResolveConversation()
  const [error, setError] = useState<string>()
  const [rejection, setRejection] = useState<ConversationResolveLocationFlagged>()

  async function resolve(resolution: ConversationResolution): Promise<void> {
    setError(undefined)
    try {
      await mutation.mutateAsync({ conversationId: conversation.convoId, resolution })
      onResolved(
        resolution.action === 'discard'
          ? t('Conversation and recording deleted.')
          : t('Location matched. Processing is queued.')
      )
    } catch (caught) {
      const response = toResponseError(caught)
      if (response.error === 'Conflict' || response.error === 'NotFound') {
        onResolved(t('This conversation has already changed or been removed. The queue has been refreshed.'))
        return
      }
      const flagged = ConversationResolveLocationFlaggedSchema.safeParse(response)
      if (flagged.success) setRejection(flagged.data)
      setError(response.message ?? t('Could not resolve this conversation. Try again.'))
    }
  }

  return (
    <Modal
      opened
      onClose={onClose}
      title={t('Review flagged conversation')}
      size='lg'
      closeOnClickOutside={!mutation.isPending}
      withCloseButton={!mutation.isPending}
      closeOnEscape={!mutation.isPending}
    >
      <Stack>
        <Text>{new Date(conversation.convoDate).toLocaleString()}</Text>
        {isDefined(conversation.downloadUrl) ? (
          <audio
            className='w-full'
            controls
            preload='none'
            src={conversation.downloadUrl}
            aria-label={t('Flagged conversation recording')}
          />
        ) : (
          <TAlert variant='warning'>
            {t('Recording unavailable. Refresh the queue to request a new playback link.')}
          </TAlert>
        )}
        {isDefined(error) && <TAlert variant='error'>{error}</TAlert>}
        <ReviewResolutionForm
          busy={mutation.isPending}
          rejection={rejection}
          onSubmit={(resolution) => void resolve(resolution)}
        />
      </Stack>
    </Modal>
  )
}
